/**
 * One plain-language sentence for what Penelope is doing on a game — the basic
 * tier's whole readout. Pure: the ticket and the strip hand it the game row's
 * facts and print what comes back. Regimes are the relay's
 * (relay/src/services/penelope/decide.ts); the words are the trader's.
 */
import type { PenelopeRegime } from '@/hooks/usePenelope';

export interface PenelopeStatusInput {
  /** cfg.enabled */
  armed: boolean;
  /** Armed but not running, and why (row.parked). */
  parked: string | null;
  /** Shadow mode: nothing reaches Kalshi. */
  practice: boolean;
  teamA: string;
  teamB: string;
  /** The baseline runner's snapshot, or null before the first tick. */
  live: {
    regime: PenelopeRegime;
    gate: string | null;
    stopReason: string | null;
    rests: { A: { px: number; size: number; why?: string } | null; B: { px: number; size: number; why?: string } | null };
    limitRejects?: number;
    lastError?: string | null;
    /** Relay ≥ 2026-10-14: why each side is (not) posting, the follow reference and its age, the last line's age, and the
     *  unpaired tail she is selling back. Older relays send none and the sentence reads as before. */
    desiredWhy?: { A: string | null; B: string | null };
    ref?: { A: { px: number; ageMs: number } | null; B: { px: number; ageMs: number } | null };
    lastFairAgeMs?: number | null;
    tail?: { team: 'A' | 'B'; contracts: number; avgCents: number } | null;
    /** Why the fair is unusable ('stale' = Pinnacle's own timestamp is older than max Pinnacle age) and that age. */
    fairBlock?: string | null;
    vendorAgeMs?: number | null;
    /** Relay ≥ 2026-10-15: Pinnacle's price is past max Pinnacle age but still used (in the feed, game moving). */
    fairStill?: { ageMs: number; driftCents: number | null } | null;
  } | null;
}

const secs = (ms: number): string => `${Math.round(ms / 1000)} s`;

/** Why a side is NOT posting, from decide's reason, in the trader's words. Null for reasons that are not worth a line
 *  (nothing to say while she is quoting, or the side is simply not armed / capped). */
function notPostingText(why: string | null | undefined): string | null {
  if (!why) return null;
  let m: RegExpMatchArray | null;
  m = why.match(/^follow: touch (\d+) under the reference (\d+)/);
  if (m) return `best bid ${m[1]}¢ is under the reference ${m[2]}¢`;
  m = why.match(/^follow: touch (\d+) more than (\d+)¢ above the reference (\d+)/);
  if (m) return `best bid ${m[1]}¢ is more than ${m[2]}¢ above the reference ${m[3]}¢`;
  if (/^follow: no reference level yet/.test(why)) return 'no reference yet (no good-value price before Pinnacle went off)';
  m = why.match(/^follow: reference (\d+) is (\d+) s old/);
  if (m) return `reference ${m[1]}¢ is ${m[2]} s old`;
  if (/sell-back waits for Pinnacle/.test(why)) return 'sell-back waits for Pinnacle';
  m = why.match(/^pair: the (?:lock|completion) (?:is|does) not (?:at|lock at) the touch \((\d+) under (\d+)\)/);
  if (m) return `the lock is ${m[1]}¢, under the best bid ${m[2]}¢`;
  m = why.match(/^edge ([-\d.]+)¢ at the touch (\d+) < ([-\d.]+)/);
  if (m) return `edge ${m[1]}¢ at ${m[2]}¢ (needs ${m[3]})`;
  if (/^(net cap|gross cap)/.test(why)) return `at the ${why}`;
  if (/^outside the entry band/.test(why)) return why;
  return null;
}

/** The lines the richer snapshot adds while Pinnacle is off: the reference per side, why a side is not posting, and the
 *  tail she is selling back. Empty on an older relay. */
function offLineDetail(i: PenelopeStatusInput): string[] {
  const live = i.live;
  if (!live) return [];
  const out: string[] = [];
  const team = (s: 'A' | 'B') => (s === 'A' ? i.teamA : i.teamB);
  for (const s of ['A', 'B'] as const) {
    if (live.rests[s]) continue;
    const ref = live.ref?.[s];
    const why = notPostingText(live.desiredWhy?.[s]);
    if (ref && why) out.push(`${team(s)}: reference ${ref.px}¢ (${secs(ref.ageMs)} ago), not posting — ${why}`);
    else if (why) out.push(`${team(s)}: not posting — ${why}`);
    else if (ref) out.push(`${team(s)}: reference ${ref.px}¢ (${secs(ref.ageMs)} ago)`);
  }
  const t = live.tail;
  if (t && t.contracts > 0) out.push(`selling ${team(t.team)} back: ${t.contracts} unpaired at ${t.avgCents.toFixed(1)}¢`);
  return out;
}

/** What a resting order is FOR, in the trader's words (PEN-01: "is this opening, reducing or pairing?"). The relay
 *  sends the decision's reason with each rest; older relays send none and the rest reads as before. */
function purposeOf(why: string | undefined, other: string): string {
  if (!why) return '';
  if (/^unwind/.test(why)) return ` (selling ${other} back)`;
  if (/^split/.test(why)) return ' (split book: both best bids, value under the ceiling)';
  if (/scratch/.test(why)) return ' (scratching out of a hung pair)';
  if (/^pair: complete/.test(why)) return ' (completing the pair)';
  if (/^pair/.test(why)) return ' (pairing)';
  if (/^follow/.test(why)) return ' (buying, following the book up)';
  if (/^value/.test(why)) return ' (buying)';
  return '';
}

function restsText(i: PenelopeStatusInput): string | null {
  const parts: string[] = [];
  const r = i.live?.rests;
  if (r?.A) parts.push(`${i.teamA} ${r.A.px}¢ ×${r.A.size}${purposeOf(r.A.why, i.teamB)}`);
  if (r?.B) parts.push(`${i.teamB} ${r.B.px}¢ ×${r.B.size}${purposeOf(r.B.why, i.teamA)}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Why a resting order is being held, from the gate text, in the trader's words. */
function holdReason(gate: string | null): string {
  if (!gate) return 'a thin book';
  if (/one-sided/.test(gate)) return 'a one-sided book';
  if (/stale/.test(gate)) return 'a quiet book';
  if (/spread/.test(gate)) return 'a wide spread';
  if (/depth/.test(gate)) return 'a depth dropout';
  return 'a thin book';
}

export function penelopeStatusSentence(i: PenelopeStatusInput): string {
  const suffix = i.practice ? ' (practice)' : '';
  if (!i.armed) return `stopped: not quoting${suffix}`;
  if (i.parked) return `not running: ${i.parked}`;
  const live = i.live;
  if (!live) return `starting…${suffix}`;
  const rests = restsText(i);
  let body: string;
  switch (live.regime) {
    case 'STOPPED':
      body = `stopped: ${live.stopReason ?? live.gate ?? 'game over'}`;
      break;
    case 'IDLE':
      body = `waiting: ${live.gate ?? 'game not started'}`;
      break;
    case 'PAUSED':
      body = `standing aside: ${live.gate ?? 'checking the book'}`;
      break;
    case 'HOLD':
      body = rests ? `holding ${rests} through ${holdReason(live.gate)}` : `nothing resting through ${holdReason(live.gate)}`;
      break;
    case 'PULL':
      body = rests ? `holding ${rests} through a Pinnacle pull` : 'Pinnacle pulled its line: nothing resting';
      break;
    case 'PAIR': {
      // 'stale' is not a pull: Pinnacle's price is simply older than max Pinnacle age, so it counts as off (WKU@SHSU 10-14,
      // where the board still showed the line and the sentence said "off").
      const quiet = live.fairBlock === 'stale' && live.vendorAgeMs != null;
      const head = quiet ? `Pinnacle quiet (its price is ${secs(live.vendorAgeMs!)} old, older than max Pinnacle age, so it counts as off)` : 'Pinnacle is off';
      const since = !quiet && live.lastFairAgeMs != null ? ` (last line ${secs(live.lastFairAgeMs)} ago)` : '';
      body = rests ? `${head}${since}: ${rests}` : `${head}${since}: nothing resting yet`;
      const detail = offLineDetail(i);
      if (detail.length > 0) body += ` · ${detail.join(' · ')}`;
      break;
    }
    case 'HARVEST':
      body = rests ? `harvesting: ${rests}` : 'harvesting: nothing resting (touches do not lock, or waiting for depth)';
      break;
    case 'VALUE':
    default:
      body = rests ? `resting ${rests}` : 'watching for edge: nothing resting';
      if (live.fairStill) {
        const drift = live.fairStill.driftCents === null ? '' : `, consensus ${live.fairStill.driftCents.toFixed(1)}¢ away`;
        body += ` · Pinnacle unchanged for ${secs(live.fairStill.ageMs)} (still in the feed, game moving${drift}) — still used`;
      }
  }
  if ((live.limitRejects ?? 0) > 0 && live.lastError && /cap|limit/i.test(live.lastError)) {
    body += ` · your limit refused the last add (${live.lastError})`;
  }
  return body + suffix;
}
